import { DollarsIcon, FeesIcon, TransactionsIcon, TvlIcon, UserSIcon } from '@/assets';
import { Fuel } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { cn } from '@/lib/utils';
import { formatCurrencyCompactFixed, formatIntegerFull } from '@/utils/numberUtil';
import React from 'react';
import { twc } from 'react-twc';
import OverviewCard from './OverviewCard';

/**
 * Format change percentage with +/- sign
 */
const formatChange = (change: number): string => {
  if (change === 0) {
    return '0%';
  }
  return `${change > 0 ? '+' : ''}${change.toFixed(2)}%`;
  // return `${formatDisplayNumber({ num: change, showPositive: true, significantDigits: 4 })}%`;
};

const PercentageWrraper: React.FC<{ value: number }> = ({ value }) => {
  return (
    <span
      className={cn(
        'text-text-main-body',
        value > 0 && 'text-function-green-6',
        value < 0 && 'text-function-red-6'
      )}
    >
      {formatChange(value)}
    </span>
  );
};

/**
 * Overview metrics section component
 * Displays the overview metrics in a structured grid layout
 * Responsive: stacks vertically on mobile, side-by-side on desktop
 * Uses real data from Analytics API
 */
const OverviewSection: React.FC = () => {
  const { data, loading, error } = useAnalytics();

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-text-main-body">Loading overview data...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-function-red-6">Error: {error.message}</div>
      </div>
    );
  }

  // No data state
  if (!data?.overview?.data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-text-main-body">No overview data available</div>
      </div>
    );
  }

  const overview = data.overview.data;

  // Large card metrics (left column)
  const primaryMetrics = [
    {
      title: 'Cumulative Trading Volume',
      subtitle: (
        <>
          <PercentageWrraper value={overview.cumulativeTradingVolumeChange} /> vs last week
        </>
      ),
      value: formatCurrencyCompactFixed(Number(overview.cumulativeTradingVolume)),
      icon: (
        <IconWrapper>
          <DollarsIcon className="w-6 h-6 text-icon-grey" />
        </IconWrapper>
      ),
    },
    {
      title: 'TVL',
      subtitle: (
        <>
          <PercentageWrraper value={overview.tvlChange} /> vs last week
        </>
      ),
      value: formatCurrencyCompactFixed(overview.tvl),
      icon: (
        <IconWrapper>
          <TvlIcon className="w-6 h-6 text-icon-grey" />
        </IconWrapper>
      ),
    },
  ];

  // Top row metrics (right section, top)
  const topRowMetrics = [
    {
      title: 'Total Users',
      subtitle: (
        <>
          <PercentageWrraper value={overview.totalUsersChange} /> vs last week
        </>
      ),
      value: formatIntegerFull(overview.totalUsers),
      icon: (
        <IconWrapper>
          <UserSIcon className="w-6 h-6 text-icon-grey" />
        </IconWrapper>
      ),
    },
    {
      title: 'Daily Active Users',
      subtitle: (
        <>
          <PercentageWrraper value={overview.dailyActiveUsersChange} /> vs yesterday
        </>
      ),
      value: formatIntegerFull(overview.dailyActiveUsers),
    },
    {
      title: '7D Active Users',
      subtitle: 'Last 7 days',
      value: formatIntegerFull(overview.activeUsers7D),
    },
    {
      title: '30D Active Users',
      subtitle: 'Last 30 days',
      value: formatIntegerFull(overview.activeUsers30D),
    },
  ];

  // Bottom row metrics (right section, bottom)
  const bottomRowMetrics = [
    {
      title: 'Total Generated Fees',
      subtitle: (
        <>
          <PercentageWrraper value={overview.totalFeesUsdChange} /> vs last week
        </>
      ),
      value: `$${formatIntegerFull(overview.totalFeesUsd)}`,
      icon: (
        <IconWrapper>
          <FeesIcon className="w-6 h-6 text-icon-grey" />
        </IconWrapper>
      ),
    },
    {
      title: 'Total Transactions',
      subtitle: (
        <>
          <PercentageWrraper value={overview.totalTransactionsChange} /> vs last week
        </>
      ),
      value: formatIntegerFull(overview.totalTransactions),
      icon: (
        <IconWrapper>
          <TransactionsIcon className="w-6 h-6 text-icon-grey" />
        </IconWrapper>
      ),
    },
    {
      title: 'Total Gas Consumption',
      subtitle: (
        <>
          <PercentageWrraper value={overview.totalGasNativeChange} /> vs last week
        </>
      ),
      value: `${formatIntegerFull(overview.totalGasNative)} MON`,
      icon: (
        <IconWrapper>
          <Fuel className="w-6 h-6 text-icon-grey" />
        </IconWrapper>
      ),
    },
  ];

  return (
    <div className="flex flex-col xl:flex-row gap-4">
      {/* Left column - Large card with 2 metrics */}
      <Card className="xl:w-[300px] shrink-0 p-6 bg-solid-white-card">
        <div className="flex flex-col h-full">
          <OverviewCard {...primaryMetrics[0]} />
          <div className="w-full h-px bg-border-light-line-1 my-auto" />
          <OverviewCard {...primaryMetrics[1]} />
        </div>
      </Card>

      {/* Right section - Grid of metrics */}
      <div className="flex-1 flex flex-col gap-4">
        {/* Top row - 4 metrics (2 cols on mobile, 4 cols on desktop) */}
        <Card className="p-6 bg-solid-white-card lg:h-[124px]">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-0">
            {topRowMetrics.map((metric, index) => (
              <div
                key={index}
                className={cn(
                  'px-0 pr-6',
                  index > 0 ? 'lg:border-l lg:border-border-light-line-1 px-6' : ''
                )}
              >
                <OverviewCard {...metric} />
              </div>
            ))}
          </div>
        </Card>

        {/* Bottom row - 3 metrics (1 col on mobile, 3 cols on desktop) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {bottomRowMetrics.map((metric, index) => (
            <Card key={index} className="p-6 bg-solid-white-card lg:h-[124px]">
              <OverviewCard {...metric} />
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

const IconWrapper = twc.div`w-9 h-9 rounded-md bg-solid-white-card border border-solid border-border-light-line-1 flex items-center justify-center`;

export default OverviewSection;
